import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { legendConfigs } from './legend-configs';
import {
  isDailyDeviationBandLegend,
  isWeatherLegendGradient,
  matchWeatherLegendKey,
  weatherLegendCssBaseName,
  weatherLegendTitleKey
} from './legend-match.util';
import {clipLegendToRange, formatLegendScaleLabel, LegendStop, LegendValueRange} from './legend-range.util';

@Component({
  selector: 'app-weather-legend',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './weather-legend.component.html',
  styleUrls: ['./weather-legend.component.scss']
})
export class WeatherLegendComponent implements AfterViewInit, OnChanges, OnDestroy {
  private translate = inject(TranslateService);
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly zone = inject(NgZone);

  /** Must match dock reserves in weather-legend.component.scss (left switcher + cursor coords). */
  private static readonly DOCK_HORIZONTAL_RESERVE = 72 + 20 + 306 + 8;
  /** Gradient scale min-width (~400) + title + padding — below this, dock would clip content. */
  private static readonly MIN_DOCK_CONTENT_WIDTH = 480;

  @Input() selectedWeatherLayer: string | null = null;
  @Input() visible: boolean = false;
  /** When true, legend sits above the satellite timeline; otherwise it occupies the timeline slot. */
  @Input() timelineActive: boolean = false;
  /** Hide the catalog scale until the painted min/max arrives (docs/WEATHER.md, Legend). */
  @Input() clipToDisplayedRange = false;
  @Input() displayedRange: LegendValueRange | null = null;
  /** Embedded inside General GIS (or other) legend panel — static layout, no map dock positioning. */
  @Input() embedded = false;

  legendItems: LegendStop[] = [];
  /** Inline gradient cropped to displayedRange. Null keeps the catalog CSS scale. */
  displayedGradient: string | null = null;

  /** True when the map is too narrow to show the full legend beside map-cursor-coordinates. */
  spaceConstrained = false;

  private resizeObserver?: ResizeObserver;

  /** Raised for timeline, or when the bottom dock slot is too narrow. */
  get isRaised(): boolean {
    if (this.embedded) {
      return false;
    }
    return this.timelineActive || this.spaceConstrained;
  }

  ngOnChanges(): void {
    this.rebuildLegend();
  }

  ngAfterViewInit(): void {
    if (this.embedded) {
      return;
    }
    const mapContainer =
      this.host.nativeElement.closest('.map-container') ?? this.host.nativeElement.parentElement;
    if (!mapContainer) {
      return;
    }

    this.zone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver((entries) => {
        const width = entries[0]?.contentRect.width ?? (mapContainer as HTMLElement).clientWidth;
        const available = width - WeatherLegendComponent.DOCK_HORIZONTAL_RESERVE;
        const next = available < WeatherLegendComponent.MIN_DOCK_CONTENT_WIDTH;
        if (next === this.spaceConstrained) {
          return;
        }
        this.zone.run(() => {
          this.spaceConstrained = next;
          this.cdr.markForCheck();
        });
      });
      this.resizeObserver.observe(mapContainer);
    });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  /**
   * Get weather legend title HTML
   */
  getWeatherLegendTitleHtml(): string | null {
    const title = this.getWeatherLegendTitle();
    if (title) {
      return title.replace(/\n/g, '<br>');
    }
    return null;
  }

  /**
   * Get weather legend title
   */
  getWeatherLegendTitle(): string | null {
    const translationKey = weatherLegendTitleKey(this.selectedWeatherLayer);
    return translationKey ? this.translate.instant(translationKey) : null;
  }

  private catalogItems(): LegendStop[] {
    const configKey = matchWeatherLegendKey(this.selectedWeatherLayer);
    const config = configKey
      ? legendConfigs[configKey]
      : (this.selectedWeatherLayer ? legendConfigs[this.selectedWeatherLayer] : null);
    return config?.items ?? [];
  }

  private rebuildLegend(): void {
    const catalog = this.catalogItems();
    if (isDailyDeviationBandLegend(this.selectedWeatherLayer)) {
      this.legendItems = catalog;
      this.displayedGradient = null;
      return;
    }
    if (!this.clipToDisplayedRange) {
      this.legendItems = catalog;
      this.displayedGradient = null;
      return;
    }
    if (!this.displayedRange) {
      this.legendItems = [];
      this.displayedGradient = null;
      return;
    }
    const clipped = clipLegendToRange(catalog, this.displayedRange);
    this.legendItems = clipped.clipped ? clipped.items : catalog;
    this.displayedGradient = clipped.gradient;
  }

  /**
   * Get translated value for legend item
   * If the value is a translation key, translate it; otherwise return as-is
   */
  formatLegendScaleValue(value: string): string {
    return formatLegendScaleLabel(value);
  }

  getTranslatedValue(value: string): string {
    // Check if the value looks like a translation key (uppercase with underscores)
    if (value && /^[A-Z_]+$/.test(value)) {
      return this.translate.instant(value);
    }
    return value;
  }

  /**
   * Check if the selected weather layer should display as a gradient
   */
  get isGradientWeatherLayer(): boolean {
    return isWeatherLegendGradient(this.selectedWeatherLayer);
  }

  /**
   * Check if the selected weather layer should display as discrete scale
   */
  get isDiscreteScaleWeatherLayer(): boolean {
    return !this.isGradientWeatherLayer;
  }

  /** Discrete legends (e.g. three deviation bands) shrink to content instead of filling the dock. */
  get isCompactLegend(): boolean {
    return !this.embedded && this.isDiscreteScaleWeatherLayer && this.legendItems.length > 0;
  }

  /**
   * Get the base name of the weather layer for CSS class
   */
  get weatherLayerBaseName(): string {
    return weatherLegendCssBaseName(this.selectedWeatherLayer);
  }

  /**
   * Check if legend should be visible
   */
  get isVisible(): boolean {
    return !!(
      this.visible
      && this.selectedWeatherLayer
      && this.legendItems.length
      && this.getWeatherLegendTitleHtml()
    );
  }

}
